import { useEffect, useState, type FormEvent } from 'react';
import BrandMark from './BrandMark';
import { RateLimitedError, api } from './api';

interface LoginProps {
  onSignedIn: () => void;
  onForgotPassword: () => void;
}

/**
 * شاشة تسجيل الدخول — عربية RTL.
 *
 * لا تخزّن الواجهة أى سر: الخادم يضع كوكى جلسة httpOnly، فكلمة السر تغادر
 * الحالة فور إرسالها، ولا رمز فى localStorage تقرؤه ثغرة XSS.
 */
export default function Login({ onSignedIn, onForgotPassword }: LoginProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // الرابط يظهر فقط حين يقول الخادم إن البريد مضبوط — لا وعد برسالة لن تصل
  const [resetEnabled, setResetEnabled] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .passwordResetStatus()
      .then((status) => {
        if (!cancelled) setResetEnabled(status.enabled);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      await api.login({ email: email.trim().toLowerCase(), password });
      // كلمة السر لا تبقى فى ذاكرة المكوّن بعد نجاح الدخول
      setPassword('');
      /*
       * نجاح كلمة السر لا يعنى جلسة كاملة: من يلزمه عامل ثانٍ تُصدَر له جلسة
       * جزئية. لا تقرأ الواجهة `status` من هنا، بل تسأل /auth/me فيأتى الفرض
       * من البوابة نفسها — فمصدر الحقيقة واحد ولا يتفرّع فى العميل.
       */
      onSignedIn();
    } catch (caught) {
      // الخادم يردّ برسالة واحدة لكل أسباب الفشل حتى لا يُكشف وجود البريد
      setError(messageFor(caught));
      setPassword('');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="login-screen">
      <form className="login-card" onSubmit={handleSubmit}>
        <div className="logo">
          <BrandMark />
          <span className="logo-text">
            حسابات<span className="logo-dot"> • </span>نواة
          </span>
        </div>
        <h1>تسجيل الدخول</h1>
        <p className="sub">ادخل ببيانات الحساب الذى أنشأه المالك بأمر التأسيس.</p>

        <label htmlFor="email">البريد الإلكترونى</label>
        <input
          id="email"
          name="email"
          type="email"
          dir="ltr"
          autoComplete="username"
          required
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />

        <label htmlFor="password">كلمة السر</label>
        <input
          id="password"
          name="password"
          type="password"
          autoComplete="current-password"
          required
          minLength={12}
          value={password}
          onChange={(event) => setPassword(event.target.value)}
        />

        {error && (
          <p className="error" role="alert">
            {error}
          </p>
        )}

        <button type="submit" className="primary" disabled={submitting}>
          {submitting ? 'جارى التحقق…' : 'دخول'}
        </button>

        {resetEnabled && (
          <p className="login-hint">
            <button type="button" className="link" onClick={onForgotPassword}>
              نسيت كلمة السر؟
            </button>
          </p>
        )}

        <p className="sub login-hint">
          الجلسة تنتهى بعد ١٢ ساعة، أو بعد ٣٠ دقيقة بلا نشاط. أدوار المالك والمحاسب تستوجب تحققًا
          بخطوتين.
        </p>
      </form>
    </div>
  );
}

/**
 * الرسالة تتبع سبب الرفض: الخادم يردّ رسالة واحدة لكل أسباب فشل الاعتماد حتى
 * لا يُكشف وجود البريد، لكن **تجاوز الحد ليس فشل اعتماد** — وإظهاره كـ«عطل فى
 * الخدمة» يدفع المستخدم لإعادة المحاولة فورًا فيُطيل حظره.
 */
function messageFor(caught: unknown): string {
  if (caught instanceof RateLimitedError) {
    return 'محاولات كثيرة. انتظر ' + caught.retryAfterSeconds + ' ثانية ثم أعد المحاولة.';
  }
  if (caught instanceof Error && caught.name === 'UnauthorizedError') {
    return 'البريد أو كلمة السر غير صحيحة.';
  }
  return 'تعذر الاتصال بالخدمة. تأكد أن الـ API يعمل.';
}
